export interface SuperformulaParams {
    m: number;
    a: number;
    b: number;
    n1: number;
    n2: number;
    n3: number;
    /** Canvas half-extent. viewBox will be -size/2 -size/2 size size. */
    size: number;
    /** Number of evenly-spaced θ samples in [0, 2π). */
    samples: number;
}

export interface SuperformulaPreset {
    name: string;
    m: number;
    a: number;
    b: number;
    n1: number;
    n2: number;
    n3: number;
}

export const SUPERFORMULA_PRESETS: SuperformulaPreset[] = [
    { name: "Squircle",  m: 4,  a: 1,   b: 1,   n1: 2,   n2: 2,   n3: 2   },
    { name: "Flower 5",  m: 5,  a: 1,   b: 1,   n1: 1,   n2: 1,   n3: 1   },
    { name: "Star 5",    m: 5,  a: 1,   b: 1,   n1: 0.5, n2: 1.7, n3: 1.7 },
    { name: "Pentagon",  m: 5,  a: 1,   b: 1,   n1: 100, n2: 100, n3: 100 },
    { name: "Snowflake", m: 6,  a: 1,   b: 1,   n1: 0.5, n2: 0.5, n3: 0.5 },
    { name: "Leaf",      m: 2,  a: 1,   b: 1,   n1: 1,   n2: 4,   n3: 8   },
    { name: "3-lobe",    m: 3,  a: 1,   b: 1,   n1: 4.5, n2: 10,  n3: 10  },
    { name: "Amoeba",    m: 7,  a: 1,   b: 1.3, n1: 3,   n2: 6,   n3: 10  },
];

/**
 * Evaluate the Gielis superformula at angle θ.
 * Returns the radial distance r, or NaN/Infinity for degenerate inputs.
 *
 *   r(θ) = (|cos(m·θ/4)/a|^n2 + |sin(m·θ/4)/b|^n3) ^ (-1/n1)
 */
function superformulaR(
    theta: number,
    m: number,
    a: number,
    b: number,
    n1: number,
    n2: number,
    n3: number,
): number {
    const cosT = Math.abs(Math.cos((m * theta) / 4) / a);
    const sinT = Math.abs(Math.sin((m * theta) / 4) / b);
    const sum = Math.pow(cosT, n2) + Math.pow(sinT, n3);
    if (sum === 0) return Infinity;
    return Math.pow(sum, -1 / n1);
}

/**
 * Compute a normalized SVG path `d` string for the superformula.
 *
 * All radii are divided by max(r) so the shape always fits inside the
 * half-extent `size/2`, regardless of parameter values.
 * Degenerate (NaN/Infinity) samples are skipped; if fewer than 2 valid
 * samples remain the function returns an empty string.
 */
export function computeSuperformulaPath(p: SuperformulaParams): string {
    const { m, a, b, n1, n2, n3, size, samples } = p;
    const hs = size / 2;

    // First pass: compute all radii
    const radii: number[] = new Array(samples);
    let maxR = 0;
    for (let i = 0; i < samples; i++) {
        const theta = (i / samples) * 2 * Math.PI;
        const r = superformulaR(theta, m, a, b, n1, n2, n3);
        radii[i] = r;
        if (isFinite(r) && r > maxR) maxR = r;
    }

    if (maxR === 0) return "";

    // Second pass: build path, normalizing by maxR
    const pts: string[] = [];
    for (let i = 0; i < samples; i++) {
        const r = radii[i];
        if (!isFinite(r) || isNaN(r)) continue;
        const theta = (i / samples) * 2 * Math.PI;
        const rNorm = r / maxR;
        const x = rNorm * Math.cos(theta) * hs;
        const y = rNorm * Math.sin(theta) * hs;
        pts.push(`${x.toFixed(3)},${y.toFixed(3)}`);
    }

    if (pts.length < 2) return "";
    return `M${pts.join("L")}Z`;
}
